import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Breadcrumbs,
    Container
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Download, Refresh } from '@mui/icons-material';
import { reportAPI } from '../../services/reportAPI';
import { formatDate } from '../../utils/dateUtils';

interface CoachData {
    coachKey: string;
    coachName: string;
    isGuestCoach: boolean;
    coachEmail: string;
    coachingSessionHours: number;
    groupPlayHours: number;
    coachingProgramHours: number;
    totalHours: number;
}

interface CoachingReportData {
    coaches: CoachData[];
    totals: {
        coachingSessionHours: number;
        groupPlayHours: number;
        coachingProgramHours: number;
        totalHours: number;
    };
    dateRange: {
        startDate: string;
        endDate: string;
    };
}

export const CoachingReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reportData, setReportData] = useState<CoachingReportData | null>(null);
    
    // Date range state (default to last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    const [startDate, setStartDate] = useState<Date | null>(thirtyDaysAgo);
    const [endDate, setEndDate] = useState<Date | null>(today);
    const [queryLimit] = useState<number>(5000);

    useEffect(() => {
        loadReportData();
    }, []);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Validate date range
            if (startDate && endDate) {
                const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                if (diffDays > 90) {
                    setError('Date range cannot exceed 90 days (3 months)');
                    setLoading(false);
                    return;
                }
            }

            const params = {
                startDate: startDate ? startDate.toISOString().split('T')[0] : undefined,
                endDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
                limit: queryLimit
            };

            const data = await reportAPI.getCoachingReport(params);
            setReportData(data);
        } catch (err) {
            setError('Failed to load coaching report data');
            console.error('Error loading coaching report:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = () => {
        loadReportData();
    };

    const validateDateRange = (start: Date | null, end: Date | null): boolean => {
        if (!start || !end) return true;
        
        const threeMonthsFromStart = new Date(start);
        threeMonthsFromStart.setMonth(start.getMonth() + 3);
        
        return end <= threeMonthsFromStart;
    };

    const formatHours = (hours: number): string => {
        return hours.toFixed(2);
    };

    // Define DataGrid columns
    const columns: GridColDef[] = [
        {
            field: 'coachName',
            headerName: 'Coach',
            width: 200
        },
        {
            field: 'isGuestCoach',
            headerName: 'Type',
            width: 80,
            valueFormatter: (value) => value ? 'Guest' : 'Staff'
        },
        {
            field: 'coachingSessionHours',
            headerName: 'Coaching Sessions (hrs)',
            type: 'number',
            width: 130,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatHours(value)
        },
        {
            field: 'groupPlayHours',
            headerName: 'Group Play (hrs)',
            type: 'number',
            width: 110,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatHours(value)
        },
        {
            field: 'coachingProgramHours',
            headerName: 'Coaching Programs (hrs)',
            type: 'number',
            width: 140,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatHours(value)
        },
        {
            field: 'totalHours',
            headerName: 'Total Hours',
            type: 'number',
            width: 100,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: (value) => formatHours(value),
            cellClassName: 'font-weight-bold'
        }
    ];

    const exportToCSV = () => {
        if (!reportData) return;

        const headers = ['Coach Name', 'Type', 'Email', 'Coaching Sessions (hrs)', 'Group Play (hrs)', 'Coaching Programs (hrs)', 'Total Hours'];
        const rows = reportData.coaches.map(coach => [
            coach.coachName,
            coach.isGuestCoach ? 'Guest' : 'Staff',
            coach.coachEmail || 'N/A',
            formatHours(coach.coachingSessionHours),
            formatHours(coach.groupPlayHours),
            formatHours(coach.coachingProgramHours),
            formatHours(coach.totalHours)
        ]);

        // Add totals row
        rows.push([
            'TOTAL',
            '',
            '',
            formatHours(reportData.totals.coachingSessionHours),
            formatHours(reportData.totals.groupPlayHours),
            formatHours(reportData.totals.coachingProgramHours),
            formatHours(reportData.totals.totalHours)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `coaching-report-${reportData.dateRange.startDate}-to-${reportData.dateRange.endDate}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading && !reportData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Coaching Report</Typography>
            </Breadcrumbs>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Coaching Report</Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={loadReportData}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    {reportData && (
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={exportToCSV}
                        >
                            Export CSV
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Date Range Filters */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Report Filters
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    if (newValue && endDate && !validateDateRange(newValue, endDate)) {
                                        const maxEnd = new Date(newValue);
                                        maxEnd.setMonth(maxEnd.getMonth() + 3);
                                        setEndDate(maxEnd);
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                                maxDate={today}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small'
                                    }
                                }}
                                maxDate={today}
                                minDate={startDate || undefined}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleDateChange}
                                fullWidth
                                size="small"
                                disabled={loading || !startDate || !endDate}
                            >
                                Apply Filters
                            </Button>
                        </Grid>
                    </Grid>
                </LocalizationProvider>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Date range is limited to a maximum of 3 months.
                </Typography>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {reportData && (
                <>
                    {/* Data Grid */}
                    <Paper elevation={2} sx={{ mb: 2 }}>
                        <DataGrid
                            rows={reportData.coaches}
                            columns={columns}
                            getRowId={(row) => row.coachKey}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 25 }
                                },
                                sorting: {
                                    sortModel: [{ field: 'totalHours', sort: 'desc' }]
                                }
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 }
                                }
                            }}
                            density="compact"
                            disableRowSelectionOnClick
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    py: 1
                                },
                                '& .font-weight-bold': {
                                    fontWeight: 'bold'
                                },
                                minHeight: 400
                            }}
                        />
                    </Paper>

                    {/* Totals Summary */}
                    <Paper elevation={2} sx={{ p: 2, backgroundColor: 'grey.100' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid size={{ xs: 12, sm: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    TOTALS
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Coaching Sessions
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {formatHours(reportData.totals.coachingSessionHours)} hrs
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Group Play
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {formatHours(reportData.totals.groupPlayHours)} hrs
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 2 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Coaching Programs
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {formatHours(reportData.totals.coachingProgramHours)} hrs
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Total Hours
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {formatHours(reportData.totals.totalHours)} hrs
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Box mt={2}>
                        <Typography variant="body2" color="textSecondary">
                            Report generated for: {formatDate(reportData.dateRange.startDate)} to {formatDate(reportData.dateRange.endDate)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Last updated: {new Date().toLocaleString()}
                        </Typography>
                    </Box>
                </>
            )}
        </Container>
    );
};
